import { useContext } from "react";
import IOrder_Item from "@models/IOrder_Item";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import SupplierContext from "@contexts/SupplierContext";
import { getDimensionUnitAbbreviation } from "@utils/DimensionConversion";
import "./KitItemsDisplay.scss";

type Props = {
    orderItem: IOrder_Item;
};

type KitItemGroup = {
    optionId: number | null;
    items: IOrder_Item[];
};

export const KitItemsDisplay = (props: Props) => {
    const supplierModel = useContext(SupplierContext);

    const kitItems = props.orderItem?.Kit_Items || [];

    // Sections = kit builder options; child lines carry kit_option_id. Items without
    // one (legacy/plain kits) fall into an unlabeled group and render as before.
    const groups: KitItemGroup[] = [];

    for (const kitItem of kitItems) {
        const optionId = kitItem.kit_option_id ?? null;
        const existing = groups.find((group) => group.optionId === optionId);

        if (existing) {
            existing.items.push(kitItem);
        } else {
            groups.push({ optionId, items: [kitItem] });
        }
    }

    const dimensionLabel = supplierModel?.length_for_depth ? "length" : "depth";

    const renderKitItemRow = (kitItem: IOrder_Item, index: number) => {
        return (
            <div key={index} className={"kit-item-row d-flex " + (kitItem.parent_order_item_id !== props.orderItem.id ? "ms-4" : "")}>
                <div>
                    [{kitItem.qty}] {kitItem.Catalog_Item?.name}
                </div>
                {kitItem.Catalog_Item?.description && <div className={"ms-2 me-2"}>-</div>}
                <div>{kitItem.Catalog_Item?.description}</div>
                {kitItem.depth != null && (
                    <div className={"ms-2 d-flex"}>
                        <OwLabel title={dimensionLabel} className={"me-1"} />
                        <div>
                            {/* Raw entered value + unit (e.g. "1500 mm") — no unit conversion */}
                            {kitItem.depth} {getDimensionUnitAbbreviation(kitItem.Catalog_Item?.dimension_unit_id)}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={"KitItemsDisplay"}>
            {kitItems.length ? (
                <div className={"mt-2"}>
                    <OwLabel title={"kit_items"} className={"me-2"} isBold={true} isUnderline={true} />
                    <div className={"d-flex flex-column"}>
                        {groups.map((group, groupIndex) => {
                            const optionName =
                                group.optionId != null
                                    ? props.orderItem.Catalog_Item?.Kit_Builder_Options?.find((option) => option.id === group.optionId)
                                          ?.name
                                    : undefined;

                            const groupQtyTotal = group.items.reduce((total, item) => total + (Number(item.qty) || 0), 0);

                            return (
                                <div key={groupIndex} className={"kit-item-group"}>
                                    {optionName && (
                                        <div>
                                            <OwLabel title={optionName} skipTranslate={true} isBold={true} />
                                        </div>
                                    )}
                                    {group.items.map((kitItem, index) => renderKitItemRow(kitItem, index))}
                                    {group.optionId != null && (
                                        <div className={"kit-item-group-total ms-4 d-flex"}>
                                            <OwLabel title={"total"} isBold={true} className={"me-1"} />
                                            <div>{groupQtyTotal}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
};
